<script setup lang="ts">
import {
  instanceOfPtwQaEquipmentWithSerialNumberDto,
  QaEquipmentRtSystemDto,
} from '@/api/generator';
import Dialog from '../Dialog/Dialog.vue';
import Notification from '../notifications/Notification.vue';
import { NotificationMessage } from '../notifications/types';
import { computed, onMounted, ref } from 'vue';
import { getModelLabel } from '@/views/qaEquipment/utils.ts';
import TextField from '../forms/TextField/TextField.vue';
import FormLabel from '../FormLabel/FormLabel.vue';
import IdentifierLabel from '../IdentifierLabel/IdentifierLabel.vue';
import Button from '../buttons/Button.vue';
import { api } from '@/api';

const props = defineProps<{
  open?: boolean;
  notifications: NotificationMessage[] | undefined;
  deviceId?: string;
}>();

const emits = defineEmits<{
  'update:open': [open: boolean];
}>();

const device = ref<QaEquipmentRtSystemDto>();

async function loadDevice() {
  if (props.deviceId) {
    const { result } = await api.qaEquipmentApi.getQaEquipmentRtSystem({
      id: props.deviceId,
    });

    device.value = result;
  }
}

onMounted(loadDevice);

const deviceInfo = computed(() => ({
  name: device.value?.name,
  iuid: device.value?.iuid,
  model: device.value ? getModelLabel(device.value) : undefined,
  referenceNumber: device.value?.typeNumber ?? undefined,
  serialNumber: instanceOfPtwQaEquipmentWithSerialNumberDto(device.value)
    ? device.value.serialNumber
    : undefined,
}));

function close() {
  emits('update:open', false);
}
</script>

<template>
  <Dialog
    :modelValue="open"
    size="medium"
    :hideClose="true"
    @update:modelValue="emits('update:open', $event)"
  >
    <template #title>
      {{ $t('Details') }}
    </template>

    <template #text>
      <Notification
        v-for="(notification, index) in notifications"
        :key="index"
        :value="notification"
        class="mb-4"
      />
      <template v-if="device">
        <v-container>
          <v-row>
            <FormLabel :label="`${$t('Name')} | ${$t('ID')}`">
              <IdentifierLabel :label="deviceInfo.name" :identifier="deviceInfo.iuid" />
            </FormLabel>
          </v-row>
        </v-container>

        <v-divider class="mt-4 mb-4" />

        <v-container class="pa-0">
          <v-row>
            <v-col>
              <TextField
                :label="$t('Model') + (device?.subModel ? ` | ${$t('SubModel')}` : '')"
                :modelValue="deviceInfo.model"
                mode="details"
                class="gutterTop"
              />
            </v-col>
          </v-row>

          <v-row>
            <v-col v-if="'typeNumber' in device && device.typeNumber">
              <TextField
                :label="$t('ArticleNumber')"
                :modelValue="deviceInfo.referenceNumber"
                class="gutterTop"
                mode="details"
              />
            </v-col>

            <v-col v-if="'serialNumber' in device && device.serialNumber">
              <TextField
                :label="$t('SerialNumber')"
                :modelValue="device.serialNumber"
                class="gutterTop"
                mode="details"
              />
            </v-col>
          </v-row>
        </v-container>
      </template>
    </template>

    <template #actions>
      <div class="d-flex justify-center w-100">
        <Button variant="secondary" :label="$t('Close')" @click="close()" />
      </div>
    </template>
  </Dialog>
</template>
